/**
 * Simple URL validator for frontend use.
 * - Auto-prepends https:// if missing for absolute URLs
 * - Allows relative URLs starting with '/'
 * - Throws if invalid or unsafe
 * @param {string} url - The URL to validate.
 * @returns {string} - The normalized, valid URL.
 * @throws {Error} - If the URL is invalid or unsafe.
 */
export const sanitizeUrl = (url) => {
  if (typeof url !== 'string' || !url.trim()) {
    throw new Error('Invalid or empty URL.');
  }

  let trimmed = url.trim();

  // Allow relative URLs starting with '/'
  if (trimmed.startsWith('/')) {
    // Validate that it's a proper relative path
    if (trimmed.length === 1 || trimmed.match(/^\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]*$/)) {
      return trimmed;
    } else {
      throw new Error('Invalid relative URL format.');
    }
  }

  // Auto-prepend https:// if no protocol is present for absolute URLs
  if (!/^https?:\/\//i.test(trimmed)) {
    trimmed = `https://${trimmed}`;
  }

  try {
    const parsed = new URL(trimmed);

    // Allow only http and https protocols
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      throw new Error(`Unsafe protocol "${parsed.protocol}". Only HTTP and HTTPS are allowed.`);
    }

    // Additional validation for hostname
    if (!parsed.hostname || parsed.hostname.length === 0) {
      throw new Error('URL must have a valid hostname.');
    }

    // Check for valid domain format (basic validation)
    const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!domainRegex.test(parsed.hostname)) {
      throw new Error('Invalid domain format in URL.');
    }

    // Check for minimum domain requirements (must have at least one dot for TLD)
    if (!parsed.hostname.includes('.') && !parsed.hostname.includes('localhost')) {
      throw new Error('URL must have a valid domain.');
    }

    return parsed.toString();
  } catch (error) {
    if (error.message.includes('Unsafe protocol') || error.message.includes('Invalid domain') || error.message.includes('URL must have')) {
      throw error;
    }
    throw new Error('Invalid URL format. Please enter a valid URL.');
  }
};

/**
 * Validates all href URLs in HTML content.
 * Collects all errors and throws them together.
 * @param {string} html - The HTML content.
 * @throws {Error} - If any URLs are invalid or unsafe.
 */
export const validateHtmlUrls = (html) => {
  if (typeof html !== 'string' || !html.trim()) return;

  const hrefRegex = /href\s*=\s*["']([^"']+)["']/gi;
  const errors = [];
  let match;

  while ((match = hrefRegex.exec(html)) !== null) {
    try {
      sanitizeUrl(match[1]);
    } catch (error) {
      errors.push(`Invalid URL: ${match[1]} - ${error.message}`);
    }
  }

  if (errors.length > 0) {
    throw new Error(errors.join('; '));
  }
};

